<template>
  <table
    :class="[
      ns.e('table'),
      ns.em('table', size || 'default'),
      border && ns.is('border'),
    ]"
    cellpadding="0"
    cellspacing="0"
  >
    <caption
      v-if="caption"
      :class="[ns.e('table-item'), ns.e('table-caption')]"
    />
    <thead
      v-if="header"
      :class="[ns.e('table-header'), background && ns.is('background')]"
    >
      <tr :class="[ns.e('table-tr')]">
        <template v-for="(col, ind) in normalizeColumns" :key="`th-${ind}`">
          <th
            :data-th="ind"
            :class="[ns.e('table-th'), ns.e('table-cell__wrapper')]"
            :style="col ? col : ''"
          >
            <span :class="[ns.e('table-item'), ns.e('table-cell')]" />
          </th>
        </template>
      </tr>
    </thead>
    <tbody :class="[ns.e('table-body')]">
      <template v-for="rind in rows" :key="`tr-${rind}`">
        <tr :data-th="rind" :class="[ns.e('table-tr')]">
          <template v-for="(col, cind) in normalizeColumns" :key="`td-${cind}`">
            <td
              v-if="
                customCellRender(rind, cind).colspan !== 0 &&
                customCellRender(rind, cind).rowspan !== 0
              "
              :data-td="cind"
              :class="[ns.e('table-td'), ns.e('table-cell__wrapper')]"
              :style="col ? col : ''"
              v-bind="customCellRender(rind, cind)"
            >
              <span :class="[ns.e('table-item'), ns.e('table-cell')]" />
            </td>
          </template>
        </tr>
      </template>
    </tbody>
    <tfoot v-if="footer" :class="[ns.e('table-footer')]">
      <tr :class="[ns.e('table-tr')]">
        <td
          :class="[ns.e('table-td')]"
          :colspan="normalizeColumns.length"
          :style="cellStyle"
        >
          <span :class="[ns.e('table-item'), ns.e('table-cell')]" />
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useNamespace } from '@element-plus/hooks'
import { skeletonTableProps } from './skeleton-table'
import type {
  CellDescriptor,
  CustomCellRenderFunction,
  StyleMap,
} from './skeleton-table'

type rowCellDescriptor = CellDescriptor[][]

defineOptions({
  name: 'ElSkeletonTable',
})
const props = defineProps(skeletonTableProps)
const ns = useNamespace('skeleton')

const cellSpans: rowCellDescriptor = []

const normalizeColumns = computed<string[]>(() => {
  let columns: any[] = []
  if (Array.isArray(props.cols)) {
    columns = props.cols.map((col: any): string => {
      let styles: StyleMap = {
        ...cellStyle.value,
      }
      if (col) {
        if (typeof col === 'object') {
          styles = { ...styles, ...col }
        } else if (typeof col === 'string') {
          styles = { ...styles, width: col }
        } else {
          styles = { ...styles, width: `${col}px` }
        }
      }
      return styleStringify(styles)
    })
  } else {
    const defaultStyle: string = styleStringify(cellStyle.value)
    for (let i = 0; i < props.cols; i++) {
      columns.push(defaultStyle)
    }
  }
  return columns
})

const cellStyle = computed<any>(() => {
  const style: { height?: string } = {}
  if (props.cellHeight) {
    style.height = `${props.cellHeight}px`
  }
  return style
})

watch(
  () => [props.rows, props.cols, props.customCellRender],
  () => {
    cellSpans.splice(0, cellSpans.length)
  }
)

const styleStringify = (styles: StyleMap = {}): string => {
  const keys: string[] = Object.keys(styles)
  const attributes: string[] = []
  keys.forEach((key: string): void => {
    attributes.push(`${key}:${styles[key]}`)
  })

  return attributes.join(';')
}

const customCellRender: CustomCellRenderFunction = (
  row: number,
  col: number
) => {
  if (cellSpans[row] && cellSpans[row][col]) {
    return cellSpans[row][col]
  } else if (!cellSpans[row]) {
    cellSpans[row] = []
  }

  let cellSpan = { colspan: 1, rowspan: 1 }
  if (props.customCellRender) {
    cellSpan = { ...cellSpan, ...props.customCellRender(row, col) }
  }
  cellSpans[row][col] = cellSpan
  return cellSpan
}
</script>
